import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@bliks-io/ui';
import { TableToolbarButton } from './TableToolbarButton';

/**
 * What "Liste leeren" actually removes — the confirm dialog states exactly
 * this. `drafts`: unsaved rows only (nothing persisted yet). `all`: the step
 * also queues already-imported rows for deletion, committed on Weiter.
 */
export type ClearScope = 'drafts' | 'all';

const DESCRIPTION_KEY: Record<ClearScope, string> = {
  drafts: 'initialSetup.clearConfirm.description',
  all: 'initialSetup.clearConfirm.deleteAllDescription'
};

interface ClearDraftsButtonProps {
  onClear: () => void;
  scope?: ClearScope;
}

/**
 * The quiet "Liste leeren" of every wizard draft table. Clearing throws away
 * everything the user uploaded or typed, so it always confirms first.
 */
export function ClearDraftsButton({ onClear, scope = 'drafts' }: ClearDraftsButtonProps) {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  return (
    <>
      <TableToolbarButton
        className="border-line text-fg-secondary"
        onClick={() => setIsConfirmOpen(true)}
      >
        <Trash2 className="size-3.5!" />
        {t('initialSetup.clearDrafts')}
      </TableToolbarButton>
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        icon={<Trash2 className="h-5 w-5" />}
        iconTone="danger"
        variant="danger"
        title={t('initialSetup.clearConfirm.title')}
        description={t(DESCRIPTION_KEY[scope])}
        confirmLabel={t('initialSetup.clearConfirm.confirm')}
        onConfirm={() => {
          setIsConfirmOpen(false);
          onClear();
        }}
      />
    </>
  );
}
